import { call, put, takeLatest, select } from "redux-saga/effects";
import get from "lodash/get";
import Requester from "common/network/http/Request";
import ListingActions, { ListingTypes } from "reducers/ListingRedux";
import FarmActions, { FarmSelectors } from "reducers/FarmRedux";
import endpoints from "configs/endpoints";
import { history } from "store";
import getSoilIndexByState from "themes/cashrent/components/map-common/SoilData/getSoilIndexByState";
import { CreateFarmInput } from "schemas/SharedSchemas";

function* handleCreateListing(action) {
  const { data } = action;
  const isAgricultural = data.type === "AGRICULTURAL";
  const farm = yield select(FarmSelectors.farm);
  const farmClean = CreateFarmInput.clean(farm);
  const { stateId, city, soilFeatures, aerialFeatures, ...farmData } = farmClean;
  farm.location.zipCode = data.zipCode;

  const input = {
    type: data.type,
    transactionType: "LEASE",
    farm: {
      ...farmData,
      cityId: data.cityId,
    },
  };

  try {
    const result = yield call(Requester.post.bind(Requester), endpoints.listing.create, input);

    if (!get(result, "data.error")) {
      yield call(history.push, `/my-account/listing/${result.id}/information?step=${isAgricultural ? 3 : 2}`);
      yield put(ListingActions.createListingSuccess());
    } else {
      yield put(ListingActions.createListingFailure());
    }
  } catch (err) {
    yield put(ListingActions.createListingFailure());
  }
}

function* handleFetchListing(action) {
  const { id } = action;
  try {
    const result = yield call(Requester.get.bind(Requester), endpoints.listing.details(id));
    const error = get(result, "data.error");
    if (result.status === 200) {
      const listing = result.data;

      const aerialMap = get(listing, "farm.aerialMap");
      const soilMap = get(listing, "farm.soilMap");

      yield put(FarmActions.setFarmMapData("aerialMap", aerialMap));
      yield put(FarmActions.setFarmLocation({
        stateId: get(listing, "farm.city.state.id"),
        cityId: get(listing, "farm.city.id"),
        state: get(listing, "farm.city.state.name"),
        city: get(listing, "farm.city.name"),
        lat: get(listing, "farm.lat"),
        lng: get(listing, "farm.lng"),
        address: get(listing, "farm.address"),
      }));

      if (soilMap !== null) {
        yield put(ListingActions.fetchSoilAreas(listing));
      }

      yield put(ListingActions.fetchListingDetailsSuccess(listing));
    } else {
      if (error === "Listing not found") {
        yield call(history.push, "/my-account/listing/all");
      }
      yield put(ListingActions.fetchListingDetailsFailure());
    }
  } catch (err) {
    yield put(ListingActions.fetchListingDetailsFailure());
  }
}

function* handleUpdateListing(action) {
  const { id, data, callback } = action;
  const input = {
    title: data.title,
    price: parseFloat(data.lease_price),
    duration: data.lease_terms,
    leaseStartDate: data.lease_start_date,
    startDate: data.start_date,
    biddingEnd: data.bidding_end,
    description: data.description,
    legalDescription: data.legal_description,
    contractTerms: data.contract_terms,
    contractId: data.contract_language_id || null,
    totalHunter: data.total_hunter,
    groundOptions: data.ground_options,
    priceType: data.priceType.toUpperCase(),
    paymentsSchedule: data.paymentsSchedule,
  };

  if (!data.isAgricultural) {
    input.insurance = data.insurance;
  }

  try {
    const result = yield call(Requester.put.bind(Requester), endpoints.listing.update(id), input);
    if (!get(result, "data.error")) {
      yield put(ListingActions.updateListingDetailsSuccess(data));
      callback();
    } else {
      yield put(ListingActions.updateListingDetailsFailure());
    }
  } catch (err) {
    yield put(ListingActions.updateListingDetailsFailure());
  }
}

function* handleUpdateListingHuntingInsurance(action) {
  const { id, data } = action;
  const {
    has_docks_or_piers,
    docks_or_piers_count,
    boats_can_dock_count,
    has_lakes_or_ponds,
    lakes_or_ponds_count,
    has_boats_for_hunters,
    boats_for_hunters_count,
    has_provided_accomodation,
    accomodation_count,
    has_farming,
    is_hunting_permit_allowed,
  } = data;

  const listingDetails = yield select((state) => state.listing);

  const input = {
    docksOrPiersCount: has_docks_or_piers === "yes" ? docks_or_piers_count : null,
    boatsCanDockCount: has_docks_or_piers === "yes" ? boats_can_dock_count : null,
    lakesOrPondsCount: has_lakes_or_ponds === "yes" ? lakes_or_ponds_count : null,
    boatsForHuntersCount: has_boats_for_hunters === "yes" ? boats_for_hunters_count : null,
    accomodationCount: has_provided_accomodation === "yes" ? accomodation_count : null,
    farming: has_farming === "yes",
    huntingPermitAllowed: is_hunting_permit_allowed === "yes",
  };

  try {
    const result = yield call(Requester.put.bind(Requester), endpoints.listing.updateInsurance(id), input);
    if (!get(result, "data.error")) {
      yield put(ListingActions.updateListingDetailsSuccess({
        ...listingDetails,
        insurance: input,
      }));
    } else {
      yield put(ListingActions.updateListingDetailsFailure());
    }
  } catch (err) {
    yield put(ListingActions.updateListingDetailsFailure());
  }
}

function* handlePublishListing(action) {
  const { id, data } = action;
  const input = {
    shouldPublish: data.shouldPublish,
    paymentMethodId: data.payment_method_id,
  };
  try {
    const result = yield call(Requester.put.bind(Requester), endpoints.listing.publish(id), input);
    if (!get(result, "data.error")) {
      yield call(history.push, `/land-detail/${id}`);
      if (result.status === "PENDING_CONFIRMATION") {
        yield put(ListingActions.pendingListingSuccess(result.status));
      } else {
        yield put(ListingActions.publishListingSuccess(result.status));
      }
    } else {
      yield put(ListingActions.publishListingFailure());
    }
  } catch (err) {
    yield put(ListingActions.publishListingFailure());
  }
}

function* handleRemoveFile(action) {
  const { id, listing_id } = action;
  try {
    yield call(Requester.delete.bind(Requester), endpoints.listing.deleteFile(listing_id, id));
    yield put(ListingActions.removeFileSuccess(id));
  } catch (err) {
    yield put(ListingActions.removeFileFailure());
  }
}

function* handleFetchSoilAreas(action) {
  const { listing } = action;
  try {
     const stateName = get(listing, "farm.city.state.name");
  const soilMap = get(listing, "farm.soilMap");
  const soilData = yield call(getSoilIndexByState, stateName, soilMap);
  const location = {
    address: get(listing, "farm.address"),
    zipCode: get(listing, "farm.zipCode"),
    city: get(listing, "farm.city.name"),
    county: get(listing, "farm.city.county"),
    state: get(listing, "farm.city.state.name"),
  };

  yield put(FarmActions.requestSoilDataSuccess({
    soilData: {
      ...soilData,
      // Overrides the auto-value by the user input
      tillableAcres: listing.tillable_acres,
    },
    location,
  }));

  if (!listing.average_estimate_type && soilData.indexType) {
    listing.average_estimate_type = soilData.indexType;
    listing.average_estimate_index = soilData.soilIndex;
  } else if (!listing.average_estimate_type) {
    listing.average_estimate_type = "NCCPI";
    listing.average_estimate_index = 0;
  }

  yield put(FarmActions.setFarmMapData("soilMap", soilMap));
  yield put(ListingActions.fetchSoilAreasSuccess(listing));
  } catch (error) {
  yield put(ListingActions.fetchSoilAreasFailure());
  }
}

function* handleSoldListing(action) {
  const { id, data } = action;

  try {
    const result = yield call(Requester.put.bind(Requester), endpoints.listing.soldListing(id), data);
    if (result.status === 200 && !get(result, "data.error")) {
      yield put(ListingActions.soldListingSuccess());
    } else {
      yield put(ListingActions.soldListingFailure());
    }
  } catch (err) {
    yield put(ListingActions.soldListingFailure());
  }
}

export default function* () {
  return [
    yield takeLatest(ListingTypes.CREATE_LISTING, handleCreateListing),
    yield takeLatest(ListingTypes.FETCH_LISTING_DETAILS, handleFetchListing),
    yield takeLatest(ListingTypes.UPDATE_LISTING_DETAILS, handleUpdateListing),
    yield takeLatest(ListingTypes.UPDATE_LISTING_HUNTING_INSURANCE, handleUpdateListingHuntingInsurance),
    yield takeLatest(ListingTypes.PUBLISH_LISTING, handlePublishListing),
    yield takeLatest(ListingTypes.REMOVE_FILE, handleRemoveFile),
    yield takeLatest(ListingTypes.FETCH_SOIL_AREAS, handleFetchSoilAreas),
    yield takeLatest(ListingTypes.SOLD_LISTING, handleSoldListing),
  ];
}
